import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { BarChart3, TrendingUp, Clock, Activity, Package, Truck, Filter, Calendar, Download, ChevronDown, ChevronRight, AlertCircle } from 'lucide-react';
import { SmartDropdown, FilterSection, useToast, LoadingState, EmptyState } from '../components/ui';
import api from '../services/api';

const DockAnalytics = () => {
  const { success, error: showError } = useToast();

  const [filters, setFilters] = useState({
    dateRange: '30',
    dockType: 'all',
    materialType: 'all',
    status: 'all',
    minUtilization: '',
    maxUtilization: ''
  });

  const [selectedDock, setSelectedDock] = useState(null);
  const [showDrillDown, setShowDrillDown] = useState(false);

  // Fetch overall analytics
  const { data: overallAnalytics, isLoading: overallLoading } = useQuery({
    queryKey: ['dock-overall-analytics', filters.dateRange],
    queryFn: async () => {
      const response = await api.get(`/warehouse/docks/overall_analytics/?days=${filters.dateRange}`);
      return response.data;
    }
  });

  // Fetch individual dock analytics
  const { data: dockAnalytics, isLoading: dockAnalyticsLoading } = useQuery({
    queryKey: ['dock-analytics-list', filters.dateRange],
    queryFn: async () => {
      const response = await api.get('/warehouse/docks/');
      const docks = response.data.results || response.data;

      // Fetch analytics for each dock
      const analyticsPromises = docks.map(dock =>
        api.get(`/warehouse/docks/${dock.id}/analytics/?days=${filters.dateRange}`)
          .then(res => ({ ...res.data, dock_id: dock.id }))
      );

      return await Promise.all(analyticsPromises);
    }
  });

  // Fetch workload distribution
  const { data: workloadData } = useQuery({
    queryKey: ['dock-workload'],
    queryFn: async () => {
      const response = await api.get('/warehouse/docks/workload_distribution/');
      return response.data;
    }
  });

  // Fetch drill-down data for selected dock
  const { data: drillDownData } = useQuery({
    queryKey: ['dock-drilldown', selectedDock?.dock_id],
    queryFn: async () => {
      if (!selectedDock) return null;
      const response = await api.get(`/warehouse/docks/${selectedDock.dock_id}/analytics/?days=${filters.dateRange}`);
      return response.data;
    },
    enabled: !!selectedDock
  });

  // Filter dock analytics based on filters
  const filteredDockAnalytics = dockAnalytics?.filter(dock => {
    if (filters.dockType !== 'all' && dock.dock_type !== filters.dockType) return false;
    if (filters.materialType !== 'all' && dock.material_type !== filters.materialType) return false;
    if (filters.status !== 'all' && dock.current_status !== filters.status) return false;
    if (filters.minUtilization && dock.metrics?.utilization_percentage < parseFloat(filters.minUtilization)) return false;
    if (filters.maxUtilization && dock.metrics?.utilization_percentage > parseFloat(filters.maxUtilization)) return false;
    return true;
  }) || [];

  const handleDockClick = (dock) => {
    setSelectedDock(dock);
    setShowDrillDown(true);
  };

  const handleExportData = () => {
    // Create CSV export
    const csvData = filteredDockAnalytics.map(dock => ({
      'Dock Number': dock.dock_number,
      'Type': dock.dock_type,
      'Material': dock.material_type,
      'Utilization %': dock.metrics?.utilization_percentage || 0,
      'Turnaround Hours': dock.metrics?.average_turnaround_hours || 0,
      'Trucks Handled': dock.metrics?.total_trucks_handled || 0,
      'Downtime Hours': dock.metrics?.downtime_hours || 0
    }));

    const csv = [
      Object.keys(csvData[0]).join(','),
      ...csvData.map(row => Object.values(row).join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `dock-analytics-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    success('Analytics data exported successfully!');
  };

  const getUtilizationColor = (utilization) => {
    if (utilization >= 80) return 'text-green-600 bg-green-50 border-green-300';
    if (utilization >= 60) return 'text-yellow-600 bg-yellow-50 border-yellow-300';
    if (utilization >= 40) return 'text-orange-600 bg-orange-50 border-orange-300';
    return 'text-red-600 bg-red-50 border-red-300';
  };

  const filterOptions = [
    {
      name: 'dateRange',
      label: 'Date Range',
      value: filters.dateRange,
      options: [
        { value: '7', label: 'Last 7 Days' },
        { value: '30', label: 'Last 30 Days' },
        { value: '60', label: 'Last 60 Days' },
        { value: '90', label: 'Last 90 Days' }
      ]
    },
    {
      name: 'dockType',
      label: 'Dock Type',
      value: filters.dockType,
      options: [
        { value: 'LOADING', label: 'Loading' },
        { value: 'UNLOADING', label: 'Unloading' },
        { value: 'BOTH', label: 'Loading & Unloading' },
        { value: 'RAW_MATERIAL', label: 'Raw Material' },
        { value: 'FINISHED_GOODS', label: 'Finished Goods' },
        { value: 'HAZMAT', label: 'Hazardous Materials' }
      ]
    },
    {
      name: 'materialType',
      label: 'Material Type',
      value: filters.materialType,
      options: [
        { value: 'STEEL', label: 'Steel & Metal' },
        { value: 'PLASTIC', label: 'Plastic & Polymers' },
        { value: 'ELECTRONICS', label: 'Electronic Components' },
        { value: 'PACKAGING', label: 'Packaging Materials' },
        { value: 'CHEMICALS', label: 'Chemicals' },
        { value: 'FINISHED_GOODS', label: 'Finished Products' },
        { value: 'GENERAL', label: 'General Materials' },
        { value: 'HAZMAT', label: 'Hazardous Materials' }
      ]
    },
    {
      name: 'status',
      label: 'Current Status',
      value: filters.status,
      options: [
        { value: 'AVAILABLE', label: 'Available' },
        { value: 'OCCUPIED', label: 'Occupied' },
        { value: 'MAINTENANCE', label: 'Maintenance' },
        { value: 'BLOCKED', label: 'Blocked' }
      ]
    }
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Dock Analytics</h1>
          <p className="text-gray-600 mt-2">Comprehensive performance analytics and insights</p>
        </div>
        <button
          onClick={handleExportData}
          disabled={!filteredDockAnalytics.length}
          className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center space-x-2 disabled:bg-gray-300 disabled:cursor-not-allowed"
        >
          <Download className="w-4 h-4" />
          <span>Export CSV</span>
        </button>
      </div>

      {/* Summary Cards */}
      {overallLoading ? (
        <LoadingState message="Loading overall analytics..." />
      ) : overallAnalytics ? (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="card bg-gradient-to-br from-blue-50 to-blue-100 border-2 border-blue-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600 font-medium">Total Docks</p>
                <p className="text-3xl font-bold text-blue-700 mt-1">{overallAnalytics.total_docks}</p>
              </div>
              <Package className="w-10 h-10 text-blue-500 opacity-50" />
            </div>
          </div>

          <div className="card bg-gradient-to-br from-green-50 to-green-100 border-2 border-green-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-600 font-medium">Avg Utilization</p>
                <p className="text-3xl font-bold text-green-700 mt-1">{overallAnalytics.average_utilization}%</p>
              </div>
              <Activity className="w-10 h-10 text-green-500 opacity-50" />
            </div>
          </div>

          <div className="card bg-gradient-to-br from-purple-50 to-purple-100 border-2 border-purple-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-purple-600 font-medium">Total Trucks</p>
                <p className="text-3xl font-bold text-purple-700 mt-1">{overallAnalytics.total_trucks_handled}</p>
              </div>
              <Truck className="w-10 h-10 text-purple-500 opacity-50" />
            </div>
          </div>

          <div className="card bg-gradient-to-br from-yellow-50 to-yellow-100 border-2 border-yellow-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-yellow-600 font-medium">Avg Turnaround</p>
                <p className="text-3xl font-bold text-yellow-700 mt-1">{overallAnalytics.average_turnaround}h</p>
              </div>
              <Clock className="w-10 h-10 text-yellow-500 opacity-50" />
            </div>
          </div>
        </div>
      ) : null}

      {/* Filters */}
      <div className="card">
        <div className="flex items-center space-x-2 mb-4">
          <Filter className="w-5 h-5 text-gray-600" />
          <h3 className="text-lg font-semibold">Filters</h3>
        </div>

        <FilterSection
          filters={filterOptions}
          onFilterChange={(name, value) => setFilters(prev => ({ ...prev, [name]: value }))}
          onClearFilters={() => setFilters({ dateRange: '30', dockType: 'all', materialType: 'all', status: 'all', minUtilization: '', maxUtilization: '' })}
        />

        {/* Advanced Filters */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 pt-4 border-t">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Min Utilization %
            </label>
            <input
              type="number"
              min="0"
              max="100"
              value={filters.minUtilization}
              onChange={(e) => setFilters(prev => ({ ...prev, minUtilization: e.target.value }))}
              className="input"
              placeholder="e.g., 50"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Max Utilization %
            </label>
            <input
              type="number"
              min="0"
              max="100"
              value={filters.maxUtilization}
              onChange={(e) => setFilters(prev => ({ ...prev, maxUtilization: e.target.value }))}
              className="input"
              placeholder="e.g., 90"
            />
          </div>
        </div>
      </div>

      {/* Performance Table */}
      {dockAnalyticsLoading ? (
        <LoadingState message="Loading dock analytics..." />
      ) : filteredDockAnalytics.length === 0 ? (
        <EmptyState
          icon={BarChart3}
          title="No analytics data found"
          message="Try adjusting your filters to see more results"
        />
      ) : (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Dock Performance Metrics</h3>
            <span className="text-sm text-gray-600">{filteredDockAnalytics.length} dock(s) found</span>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Dock
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Material
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Utilization
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Turnaround
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Trucks
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Downtime
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredDockAnalytics.map((dock) => (
                  <tr key={dock.dock_id} className="hover:bg-gray-50 cursor-pointer" onClick={() => handleDockClick(dock)}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Package className="w-4 h-4 mr-2 text-gray-400" />
                        <div className="text-sm font-medium text-gray-900">{dock.dock_number}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-600">{dock.dock_type}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-600">{dock.material_type}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className={`text-sm font-semibold px-2 py-1 rounded ${getUtilizationColor(dock.metrics?.utilization_percentage || 0)}`}>
                          {dock.metrics?.utilization_percentage || 0}%
                        </div>
                        <div className="ml-2 w-20 bg-gray-200 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full ${
                              (dock.metrics?.utilization_percentage || 0) >= 80 ? 'bg-green-500' :
                              (dock.metrics?.utilization_percentage || 0) >= 60 ? 'bg-yellow-500' :
                              'bg-red-500'
                            }`}
                            style={{ width: `${Math.min(dock.metrics?.utilization_percentage || 0, 100)}%` }}
                          />
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-900">
                        <Clock className="w-4 h-4 mr-1 text-gray-400" />
                        {dock.metrics?.average_turnaround_hours || 0}h
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-900">
                        <Truck className="w-4 h-4 mr-1 text-gray-400" />
                        {dock.metrics?.total_trucks_handled || 0}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-red-600 font-medium">
                        {dock.metrics?.downtime_hours || 0}h
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDockClick(dock);
                        }}
                        className="text-indigo-600 hover:text-indigo-900"
                      >
                        <ChevronRight className="w-5 h-5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Performance Insights */}
      {filteredDockAnalytics.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Top Performers */}
          <div className="card border-2 border-green-200 bg-green-50">
            <div className="flex items-center space-x-2 mb-3">
              <TrendingUp className="w-5 h-5 text-green-600" />
              <h3 className="text-lg font-semibold text-green-800">Top Performers</h3>
            </div>
            <div className="space-y-2">
              {filteredDockAnalytics
                .sort((a, b) => (b.metrics?.utilization_percentage || 0) - (a.metrics?.utilization_percentage || 0))
                .slice(0, 3)
                .map((dock, index) => (
                  <div key={dock.dock_id} className="flex items-center justify-between bg-white p-2 rounded">
                    <span className="text-sm font-medium">{index + 1}. {dock.dock_number}</span>
                    <span className="text-sm font-bold text-green-600">{dock.metrics?.utilization_percentage || 0}%</span>
                  </div>
                ))}
            </div>
          </div>

          {/* Underutilized */}
          <div className="card border-2 border-orange-200 bg-orange-50">
            <div className="flex items-center space-x-2 mb-3">
              <AlertCircle className="w-5 h-5 text-orange-600" />
              <h3 className="text-lg font-semibold text-orange-800">Underutilized</h3>
            </div>
            <div className="space-y-2">
              {filteredDockAnalytics
                .filter(dock => (dock.metrics?.utilization_percentage || 0) < 50)
                .sort((a, b) => (a.metrics?.utilization_percentage || 0) - (b.metrics?.utilization_percentage || 0))
                .slice(0, 3)
                .map((dock, index) => (
                  <div key={dock.dock_id} className="flex items-center justify-between bg-white p-2 rounded">
                    <span className="text-sm font-medium">{dock.dock_number}</span>
                    <span className="text-sm font-bold text-orange-600">{dock.metrics?.utilization_percentage || 0}%</span>
                  </div>
                ))}
              {filteredDockAnalytics.filter(dock => (dock.metrics?.utilization_percentage || 0) < 50).length === 0 && (
                <p className="text-sm text-gray-600 italic">All docks well utilized</p>
              )}
            </div>
          </div>

          {/* High Downtime */}
          <div className="card border-2 border-red-200 bg-red-50">
            <div className="flex items-center space-x-2 mb-3">
              <Clock className="w-5 h-5 text-red-600" />
              <h3 className="text-lg font-semibold text-red-800">High Downtime</h3>
            </div>
            <div className="space-y-2">
              {filteredDockAnalytics
                .sort((a, b) => (b.metrics?.downtime_hours || 0) - (a.metrics?.downtime_hours || 0))
                .slice(0, 3)
                .map((dock, index) => (
                  <div key={dock.dock_id} className="flex items-center justify-between bg-white p-2 rounded">
                    <span className="text-sm font-medium">{dock.dock_number}</span>
                    <span className="text-sm font-bold text-red-600">{dock.metrics?.downtime_hours || 0}h</span>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}

      {/* Drill-down Modal */}
      {showDrillDown && selectedDock && drillDownData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto animate-scale-in">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold">Detailed Analytics: {drillDownData.dock_number}</h2>
              <button
                onClick={() => {
                  setShowDrillDown(false);
                  setSelectedDock(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                ×
              </button>
            </div>

            <div className="space-y-6">
              {/* Dock Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600">Dock Type</p>
                  <p className="text-xl font-bold text-blue-600">{drillDownData.dock_type}</p>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600">Material Type</p>
                  <p className="text-xl font-bold text-purple-600">{drillDownData.material_type}</p>
                </div>
              </div>

              {/* Performance Metrics */}
              <div className="border-t pt-4">
                <h3 className="font-semibold text-lg mb-3">Performance Metrics (Last {drillDownData.period_days} Days)</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-green-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600">Utilization</p>
                    <p className="text-2xl font-bold text-green-600">
                      {drillDownData.metrics.utilization_percentage}%
                    </p>
                    <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                      <div
                        className="bg-green-500 h-2 rounded-full"
                        style={{ width: `${drillDownData.metrics.utilization_percentage}%` }}
                      />
                    </div>
                  </div>
                  <div className="bg-yellow-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600">Avg Turnaround</p>
                    <p className="text-2xl font-bold text-yellow-600">
                      {drillDownData.metrics.average_turnaround_hours}h
                    </p>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600">Trucks Handled</p>
                    <p className="text-2xl font-bold text-blue-600">
                      {drillDownData.metrics.total_trucks_handled}
                    </p>
                  </div>
                  <div className="bg-red-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600">Downtime</p>
                    <p className="text-2xl font-bold text-red-600">
                      {drillDownData.metrics.downtime_hours}h
                    </p>
                  </div>
                </div>
              </div>

              {/* Facilities */}
              <div className="border-t pt-4">
                <h3 className="font-semibold text-lg mb-3">Facilities</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex items-center space-x-2">
                    <div className={`w-3 h-3 rounded-full ${drillDownData.facilities.has_forklift ? 'bg-green-500' : 'bg-gray-300'}`} />
                    <span>Forklift {drillDownData.facilities.has_forklift ? 'Available' : 'Not Available'}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className={`w-3 h-3 rounded-full ${drillDownData.facilities.has_weighbridge ? 'bg-green-500' : 'bg-gray-300'}`} />
                    <span>Weighbridge {drillDownData.facilities.has_weighbridge ? 'Available' : 'Not Available'}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className={`w-3 h-3 rounded-full ${drillDownData.facilities.has_loading_ramp ? 'bg-green-500' : 'bg-gray-300'}`} />
                    <span>Loading Ramp {drillDownData.facilities.has_loading_ramp ? 'Available' : 'Not Available'}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Package className="w-5 h-5 text-blue-600" />
                    <span>{drillDownData.facilities.capacity_tonnes}T Capacity</span>
                  </div>
                </div>
              </div>

              {/* Recommendations */}
              <div className="border-t pt-4 bg-blue-50 p-4 rounded-lg">
                <h3 className="font-semibold text-lg mb-2 flex items-center">
                  <TrendingUp className="w-5 h-5 mr-2 text-blue-600" />
                  Insights & Recommendations
                </h3>
                <ul className="space-y-2 text-sm">
                  {drillDownData.metrics.utilization_percentage < 50 && (
                    <li className="flex items-start">
                      <AlertCircle className="w-4 h-4 mr-2 text-orange-500 mt-0.5" />
                      <span>Low utilization detected. Consider reassigning workload or scheduling maintenance.</span>
                    </li>
                  )}
                  {drillDownData.metrics.utilization_percentage >= 80 && (
                    <li className="flex items-start">
                      <TrendingUp className="w-4 h-4 mr-2 text-green-500 mt-0.5" />
                      <span>Excellent utilization! This dock is operating at peak efficiency.</span>
                    </li>
                  )}
                  {drillDownData.metrics.downtime_hours > 24 && (
                    <li className="flex items-start">
                      <Clock className="w-4 h-4 mr-2 text-red-500 mt-0.5" />
                      <span>High downtime detected. Review maintenance schedules and operational procedures.</span>
                    </li>
                  )}
                  {drillDownData.metrics.average_turnaround_hours > 4 && (
                    <li className="flex items-start">
                      <Clock className="w-4 h-4 mr-2 text-yellow-500 mt-0.5" />
                      <span>Turnaround time is above optimal. Consider process improvements.</span>
                    </li>
                  )}
                </ul>
              </div>
            </div>

            <button
              onClick={() => {
                setShowDrillDown(false);
                setSelectedDock(null);
              }}
              className="w-full mt-6 bg-gray-200 text-gray-800 px-4 py-2 rounded hover:bg-gray-300"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DockAnalytics;
